import React, { useContext, useId } from "react"

import {
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import type { MetricRecordData } from "@/types/metrics/contracts"

import { RegexValidationContext } from "../contexts/RegexValidationContext"
import {
  FIELD_CONTAINER_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../styles"
import { RequiredMarker } from "./RequiredMarker"

interface RegexConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

const roles = [
  { key: "agent", label: "AGENT" },
  { key: "persona", label: "PERSONA" },
  { key: "tool", label: "TOOL" },
  { key: "system", label: "SYSTEM" },
  { key: "music", label: "MUSIC" },
]

const matchModes = [
  { key: "presence", label: "Presence (match found = pass)" },
  { key: "absence", label: "Absence (match NOT found = pass)" },
]

const positions = [
  { key: "any", label: "Any message" },
  { key: "first", label: "First message only" },
  { key: "last", label: "Last message only" },
]

interface RoleSelectorProps {
  id: string
  value?: string
  onChange: (role: string) => void
}

function RoleSelector({ id, value, onChange }: RoleSelectorProps) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger id={id} aria-labelledby={`${id}-label`}>
        <SelectValue placeholder="Select a role" />
      </SelectTrigger>
      <SelectContent>
        {roles.map((role) => (
          <SelectItem key={role.key} value={role.key}>
            {role.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export const RegexConfig: React.FC<RegexConfigProps> = ({
  metricRecordData,
  onChange,
}) => {
  const regexValidationError = useContext(RegexValidationContext)
  const baseId = useId()
  const roleId = `${baseId}-role`
  const matchModeId = `${baseId}-match-mode`
  const positionId = `${baseId}-position`
  const caseSensitivityId = `${baseId}-case-sensitivity`
  const handleRegexPatternChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        regex_pattern: e.target.value,
      },
    })
  }

  const handleRoleChange = (role: string) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        role: role as "agent" | "persona",
      },
    })
  }

  const handleMatchModeChange = (matchMode: string) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        match_mode: matchMode as "presence" | "absence",
      },
    })
  }

  const handlePositionChange = (position: string) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        position: position as "any" | "first" | "last",
      },
    })
  }

  const handleCaseInsensitiveToggle = (enabled: boolean) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        case_insensitive: enabled,
      },
    })
  }

  const caseInsensitive =
    metricRecordData.metric_metadata?.case_insensitive || false

  return (
    <div className="space-y-4">
      <FormField
        label={
          <>
            Regex Pattern
            <RequiredMarker />
          </>
        }
        description={
          <>
            Checked when you save.{" "}
            <a
              href="https://docs.python.org/3.12/library/re.html#regular-expression-syntax"
              target="_blank"
              rel="noopener noreferrer"
              className="text-coval-info-text underline"
            >
              Syntax reference ↗
            </a>
          </>
        }
        error={regexValidationError}
      >
        {(fieldProps) => (
          <Input
            {...fieldProps}
            type="text"
            error={Boolean(regexValidationError)}
            value={metricRecordData.metric_metadata?.regex_pattern || ""}
            onChange={handleRegexPatternChange}
            placeholder="The regex pattern to match against transcript messages"
          />
        )}
      </FormField>

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label
            htmlFor={roleId}
            id={`${roleId}-label`}
            className={LABEL_STYLES}
          >
            Role
            <RequiredMarker />
          </label>
        </div>
        <RoleSelector
          id={roleId}
          value={metricRecordData.metric_metadata?.role}
          onChange={handleRoleChange}
        />
      </div>

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label
            htmlFor={matchModeId}
            id={`${matchModeId}-label`}
            className={LABEL_STYLES}
          >
            Match Mode
          </label>
        </div>
        <Select
          value={metricRecordData.metric_metadata?.match_mode || "presence"}
          onValueChange={handleMatchModeChange}
        >
          <SelectTrigger
            id={matchModeId}
            aria-labelledby={`${matchModeId}-label`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {matchModes.map((mode) => (
              <SelectItem key={mode.key} value={mode.key}>
                {mode.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label
            htmlFor={positionId}
            id={`${positionId}-label`}
            className={LABEL_STYLES}
          >
            Position
          </label>
        </div>
        <Select
          value={metricRecordData.metric_metadata?.position || "any"}
          onValueChange={handlePositionChange}
        >
          <SelectTrigger
            id={positionId}
            aria-labelledby={`${positionId}-label`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {positions.map((pos) => (
              <SelectItem key={pos.key} value={pos.key}>
                {pos.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <span id={caseSensitivityId} className={LABEL_STYLES}>
            Case Sensitivity
          </span>
        </div>
        <div
          role="group"
          aria-labelledby={caseSensitivityId}
          className="flex h-control rounded-md bg-surface-1 p-1"
        >
          <button
            type="button"
            onClick={() => handleCaseInsensitiveToggle(false)}
            className={`flex-1 px-3 text-sm font-medium rounded-sm transition-all ${
              !caseInsensitive
                ? "bg-surface-default text-coval-text shadow-sm"
                : "text-coval-text-muted hover:text-coval-text"
            }`}
          >
            Case Sensitive
          </button>
          <button
            type="button"
            onClick={() => handleCaseInsensitiveToggle(true)}
            className={`flex-1 px-3 text-sm font-medium rounded-sm transition-all ${
              caseInsensitive
                ? "bg-surface-default text-coval-text shadow-sm"
                : "text-coval-text-muted hover:text-coval-text"
            }`}
          >
            Case Insensitive
          </button>
        </div>
      </div>
    </div>
  )
}
